// src/utils/docTemplate.js
//
// Document template profiles drive how exhibits are formatted in Microsoft Word.
// Each agency can override the app-wide default via their `doc_template` column
// on `registered_agencies`. Individual templates in the Contract Template Library
// can carry an `extracted_profile` derived by Penny from the uploaded sample.
//
// At draft time, the effective profile is resolved by precedence:
//   1. Best-matching template's `extracted_profile`        (most specific)
//   2. Agency's `doc_template` (their configured default)
//   3. APP_DEFAULT_PROFILE (Energy Safety pattern)         (fallback)
//
// `buildAndDownloadDocx` consumes the profile to render exhibits.
// Penny's drafting prompts consume `sow_section_schema` and `drafting_voice`.

export const APP_DEFAULT_PROFILE = {
  version: 1,
  name: 'Energy Safety Default',

  page: {
    size: 'letter',
    // Measured from STD213 template: 0.6" left/right, 1" top/bottom
    margins_inches: { top: 1, right: 0.6, bottom: 1, left: 0.6 },
  },

  font: {
    family: 'Arial',
    body_size_pt: 12,
    table_size_pt: 10,
  },

  spacing: {
    paragraph_after_pt: 6,
    line_spacing: 1.15,
  },

  // Right-aligned page header that appears on every page.
  // Lines support {contractor_name}, {agreement_num}, {page}, {total} substitutions.
  // For Exhibit B/D, the `exhibit_*_inserts` lines are placed BEFORE the final
  // "Page X of Y" line.
  page_header: {
    enabled: true,
    align: 'right',
    lines: [
      '{contractor_name}',
      'Contract: {agreement_num}',
      'Page {page} of {total}',
    ],
    exhibit_b_inserts: ['Exhibit B'],
    exhibit_d_inserts: ['Exhibit D'],
  },

  exhibit_titles: {
    A: { title: 'EXHIBIT A', subtitle: 'SCOPE OF WORK' },
    B: { title: 'EXHIBIT B', subtitle: 'BUDGET DETAIL AND PAYMENT PROVISIONS' },
    D: { title: 'EXHIBIT D', subtitle: 'SPECIAL TERMS AND CONDITIONS' },
  },

  // Section heading style varies per exhibit in the Energy Safety pattern:
  //   A: bold, no underline   B: bold + underlined   D: regular weight, no underline
  section_heading_style: {
    A: { bold: true,  underline: false },
    B: { bold: true,  underline: true  },
    D: { bold: false, underline: false },
  },

  // Sub-section letters (A./B./C./D.) — not bold per Energy Safety templates
  subsection_letter_bold: false,

  // Indentation in twips (1440 twips = 1 inch)
  indents_twips: {
    // Indent values measured directly from the Energy Safety STD213 template PDF.
    // OOXML w:left is RELATIVE TO THE PAGE MARGIN, not the page edge. Template
    // page margin is 0.6" (864 twips); template text positions measured from page
    // edge so subtract 864 to get the indent value to put in w:left.
    //   Page-edge → w:left value:
    //     0.625" (900)  → 36   (section heading "1. OBJECTIVE")
    //     0.875" (1260) → 396  (body / sub-letter "A.")
    //     1.125" (1620) → 756  (wrap of A. / "1)" position)
    //     1.375" (1980) → 1116 (wrap of 1) / "a)" position)
    //     1.625" (2340) → 1476 (wrap of a) / "ii)" position)
    section_heading:      36,   // marker at 0.625" from edge
    body_under_section:  396,   // body at 0.875"
    subsection_letter:   396,   // "A." marker at 0.875"; hanging indent puts wrap at 0.875"+0.25"
    sub_sub_item:        756,   // "1)" at 1.125"; hanging puts wrap at 1.375"
    sub_sub_sub_item:   1116,   // "a)" at 1.375"; hanging puts wrap at 1.625"
    roman_numeral_item: 1476,   // "ii)" at 1.625"; hanging puts wrap at 1.875"
    bullet:              396,   // bullet at 0.875"
  },

  // Contract Representatives table (CM table) styling
  cm_table: {
    enabled: true,
    fill_color_hex: 'C5E0B3',          // light green, matches Energy Safety templates
    header_all_filled: true,            // entire header row is green
    body_left_col_filled: true,         // body rows: only left col is green
    text_align: 'center',               // all cells center-aligned
    col_widths_twips: [2300, 3530, 3530], // sums to 9360 (6.5" content area)
    labels: {
      header_left:  'Representatives',
      header_state: 'Energy Safety',
      rows: [
        'Contract Manager:',
        'Address:',
        'City, State, Zip:',
        'Phone Number:',
        'E-mail Address:',
      ],
    },
  },

  // SOW section structure used by Penny's drafting prompts (session 3 wires this in).
  // `embed: 'cm_table'` directs the docx builder to insert the CM table inline
  // within section 3 instead of appending it at the end. Currently the docx
  // builder still appends — the embed switch lands in session 3.
  sow_section_schema: [
    { num: 1,  title: 'OBJECTIVE' },
    { num: 2,  title: 'TERM OF PERFORMANCE' },
    { num: 3,  title: 'CONTRACT REPRESENTATIVES', embed: 'cm_table' },
    { num: 4,  title: 'WORK LOCATION' },
    { num: 5,  title: 'SERVICE HOURS' },
    { num: 6,  title: 'AUTHORIZATION OF WORK' },
    { num: 7,  title: 'CONTRACT TASKS AND DELIVERABLES' },
    { num: 8,  title: 'AGENCY RESPONSIBILITIES' },
    { num: 9,  title: 'WARRANTY' },
    { num: 10, title: 'ACCEPTANCE CRITERIA' },
  ],

  // Drafting voice — fed into Penny's system prompts in session 3
  drafting_voice: {
    tone: 'formal California state government contract language',
    agency_name_in_text: 'Energy Safety',
    preferred_phrases: [],
    avoid_phrases: [],
  },

  // Exhibit C (GTC) is incorporated by reference per California state contract
  // convention — Penny must never flag it as missing.
  exhibit_c_handling: 'incorporated_by_reference',
};

// Returns a deep clone of the app default so callers can't mutate the constant.
export function getDefaultProfile() {
  return JSON.parse(JSON.stringify(APP_DEFAULT_PROFILE));
}

// Resolves the effective profile for a draft based on precedence rules.
//
// Args (object):
//   contractType    — string like "Consulting", "IT Maintenance", etc. (used to
//                     match Contract Template Library entries by `type`)
//   exhibit         — 'A' | 'B' | 'D' | 'SOL' (used to filter to templates that
//                     include this exhibit)
//   templates       — full visible templates array (own + agency-scope)
//   agencyProfile   — agency's `doc_template` JSON (or null)
//   currentUserId   — current user's UUID (used to prefer own templates)
//
// Returns:
//   { profile, source } where source describes which layer matched, useful for
//   logging/UX. profile is always a complete profile (default-merged).
//
// Precedence (highest wins):
//   1. Best-matching template's extracted_profile (private > agency, recent > old)
//   2. Agency's doc_template
//   3. APP_DEFAULT_PROFILE
//
// Layout-vs-content split for matched templates:
//   - DOCX-source profiles (_source: 'docx') contribute EVERYTHING — they are
//     measured exactly from the source file, so layout numerics are reliable.
//   - PDF-source profiles (_source: 'pdf' or unset legacy) only contribute
//     CONTENT fields. Layout numerics fall back to agency-profile or app-default
//     because Penny cannot reliably read indents/spacing/margins from a rendered PDF.
const LAYOUT_FIELDS = new Set(['page', 'font', 'spacing', 'indents_twips', 'page_header']);

function pickContentFieldsOnly(profile) {
  if (!profile || typeof profile !== 'object') return {};
  const out = {};
  for (const k of Object.keys(profile)) {
    if (LAYOUT_FIELDS.has(k)) continue;
    if (k === '_source') continue;
    out[k] = profile[k];
  }
  return out;
}

export function getEffectiveProfile({
  contractType = null,
  exhibit = 'A',
  templates = [],
  agencyProfile = null,
  currentUserId = null,
} = {}) {
  // Step 1: find matching templates
  const matched = (templates || []).filter(t => {
    if (!t.extractedProfile) return false;
    if (!t.exhibits || (exhibit !== 'SOL' && !t.exhibits.includes(exhibit))) return false;
    if (contractType && t.type && t.type !== contractType) return false;
    return true;
  });

  // Sort: own templates first, then by recency (updatedAt or createdAt desc)
  matched.sort((a, b) => {
    const aOwn = a.ownerId === currentUserId ? 1 : 0;
    const bOwn = b.ownerId === currentUserId ? 1 : 0;
    if (aOwn !== bOwn) return bOwn - aOwn;
    const aDate = a.updatedAt || a.createdAt || '';
    const bDate = b.updatedAt || b.createdAt || '';
    if (aDate > bDate) return -1;
    if (aDate < bDate) return 1;
    return 0;
  });

  if (matched.length > 0) {
    const top = matched[0];
    const ext = top.extractedProfile || {};

    // Both docx and PDF extraction paths now contribute CONTENT FIELDS ONLY.
    // Layout values (page, font, spacing, indents, page_header) come from the
    // agency profile or app default — never from extracted data — because
    // computing exact layout from a Word document's complex inheritance of
    // styles + numbering definitions + per-paragraph overrides is not reliable.
    let base = agencyProfile ? mergeIntoDefault(agencyProfile) : getDefaultProfile();
    base = mergeIntoDefault({ ...base, ...pickContentFieldsOnly(ext) });
    return {
      profile: base,
      source: { type: 'template', templateId: top.id, templateName: top.name, scope: top.scope, fileSource: ext._source || 'pdf' },
    };
  }
  if (agencyProfile) {
    return {
      profile: mergeIntoDefault(agencyProfile),
      source: { type: 'agency_default' },
    };
  }
  return {
    profile: getDefaultProfile(),
    source: { type: 'app_default' },
  };
}

// Legacy/test signature retained for backwards compatibility with existing
// callers that pre-date the matching logic.
export function getEffectiveProfileLegacy({ matchedTemplate, agencyProfile } = {}) {
  if (matchedTemplate?.extracted_profile) {
    return mergeIntoDefault(matchedTemplate.extracted_profile);
  }
  if (agencyProfile) {
    return mergeIntoDefault(agencyProfile);
  }
  return getDefaultProfile();
}

// Deep-merges a partial profile onto the app default so callers always receive
// a complete profile. Top-level keys in the partial override the default;
// nested objects are merged field-by-field. Arrays in the partial replace
// arrays in the default entirely (no merging).
export function mergeIntoDefault(partial) {
  const out = getDefaultProfile();
  function merge(base, over) {
    for (const k of Object.keys(over || {})) {
      const v = over[k];
      if (v === null || v === undefined) continue;
      if (Array.isArray(v)) {
        base[k] = v.slice();
      } else if (typeof v === 'object' && typeof base[k] === 'object' && !Array.isArray(base[k])) {
        merge(base[k], v);
      } else {
        base[k] = v;
      }
    }
    return base;
  }
  return merge(out, partial);
}

// ── Unit converters (twips is OOXML's basic unit; 1 inch = 1440 twips) ──
export function ptToHalfPt(pt)         { return Math.round(pt * 2); }   // for w:sz
export function ptToTwips(pt)          { return Math.round(pt * 20); }  // for w:spacing
export function inchesToTwips(inches)  { return Math.round(inches * 1440); }

// ── Header line interpolation ──
// Replaces placeholders in profile header lines:
//   {contractor_name}, {agreement_num} — text values from the contract
//   {exhibit}                          — "Exhibit A" / "Exhibit B" / "Exhibit D" / "Solicitation"
//   {exhibit_title}                    — the subtitle from exhibit_titles[exhibit]
//   {page}, {total}                    — replaced with __PAGE__/__NUMPAGES__ markers so
//                                        the docx builder can emit OOXML field codes for them
export function interpolateHeaderLine(template, vars) {
  return String(template || '')
    .replace(/\{contractor_name\}/g, vars.contractor_name || '')
    .replace(/\{agreement_num\}/g,   vars.agreement_num   || '')
    .replace(/\{exhibit\}/g,         vars.exhibit         || '')
    .replace(/\{exhibit_title\}/g,   vars.exhibit_title   || '')
    .replace(/\{page\}/g,            '__PAGE__')
    .replace(/\{total\}/g,           '__NUMPAGES__');
}
